import "dayjs/locale/en";

import { ExclamationCircleIcon } from "@heroicons/react/20/solid";
import type { PopoverProps } from "@mantine/core";
import type { DateValue } from "@mantine/dates";
import { DatePickerInput as MantineDatePickerInput, DatesProvider } from "@mantine/dates";
import type React from "react";
import type { Control } from "react-hook-form";
import { useController } from "react-hook-form";

import FormInput from "~/components/formElements/FormInput";
import type { FormInputBaseVariants } from "~/components/formElements/formInputBaseVariant.ts";
import { formInputBaseVariants } from "~/components/formElements/formInputBaseVariant.ts";
import FormInputError from "~/components/formElements/FormInputError";
import Label from "~/components/formElements/Label/Label";

type SharedPropsType = FormInputBaseVariants & {
	clearable?: boolean;
	name: string;
	disabled?: boolean;
	label?: string;
	error?: string;
	maxDate?: Date;
	minDate?: Date;
	position?: PopoverProps["position"];
	placeholder?: string;
};

type DatePickerInputPlainProps = SharedPropsType & {
	value: DateValue;
	onChange: (date: DateValue) => void;
};

type DateInputProps = SharedPropsType & {
	control: Control<any>;
};

export const DatePickerInputPlain: React.FunctionComponent<DatePickerInputPlainProps> = ({
	clearable = true,
	disabled,
	error,
	inputSize,
	label,
	maxDate,
	minDate,
	name,
	onChange,
	placeholder,
	position,
	theme,
	value,
}) => {

	const inputValue = value || null;

	return (
		<FormInput>
			{label && <Label htmlFor={name}>{label}</Label>}
			<div className="relative rounded-md shadow-sm">
				<DatesProvider settings={{ locale: "de" }}>
					<MantineDatePickerInput
						classNames={{
							input: formInputBaseVariants({ inputSize, theme, disabled }),
							section: `absolute right-0 top-0 h-full 
										flex items-center 
										pr-2
										[&_button]:rounded-full [&_button]:hover:bg-gray-200 [&_button]:w-5 [&_button]:h-5
										[&_button]:flex [&_button]:items-center [&_button]:justify-center
										[&_svg]:text-gray-500`,
							calendarHeader: "flex justify-between h-10 bg-white",
							calendarHeaderLevel: "flex gap-1 justify-center items-center hover:bg-gray-100 rounded-full px-4",
							calendarHeaderControl: "flex items-center justify-center w-10 h-10 hover:bg-gray-100 rounded-full data-[disabled]:opacity-0",
							calendarHeaderControlIcon: "text-gray-400 data-[direction='previous']:rotate-90 data-[direction='next']:-rotate-90 ",
							month: "border-collapse",
							monthsListControl: `px-2 py-1 text-center hover:bg-primary-400 hover:text-white rounded-md text-sm py-2 w-full
                                                data-[disabled="true"]:hover:bg-white data-[disabled="true"]:text-gray-300`,
							monthsList: "w-full",
							yearsListControl: `px-2 py-1 text-center hover:bg-primary-400 hover:text-white rounded-md text-sm py-2 w-full
                            data-[disabled="true"]:hover:bg-white data-[disabled="true"]:text-gray-300`,
							yearsList: "w-full",
							weekday: "text-gray-400 capitalize font-light text-sm",
							day: `w-8 h-8 inline-flex items-center justify-center rounded-md
									text-sm
									hover:bg-primary-400 hover:text-white  
									data-[outside]:text-gray-500 
									data-[disabled]:text-gray-300 data-[disabled]:hover:bg-white   
									data-[selected]:bg-primary-500 data-[selected]:text-white
									data-[weekend]:text-accent-500 data-[weekend]:hover:text-white
							`,

						}}
						popoverProps={{
							position: position || "bottom-start",
							classNames: {
								dropdown: "absolute z-50 border border-gray-200 bg-white rounded-md shadow-lg p-2 text-gray-800 min-w-[16rem]",
							},
						}}

						valueFormat="DD. MMMM YYYY"
						name={name}
						//@ts-expect-error Mantine types are not up to date
						placeholder={placeholder || "Datum auswählen"}
						maxDate={maxDate}
						minDate={minDate}
						disabled={disabled}
						value={inputValue}
						onChange={onChange}
						clearable={clearable}
					/>
				</DatesProvider>
				{error && (
					<div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
						<ExclamationCircleIcon className="size-5 text-red-500"
											   aria-hidden="true" />
					</div>
				)}
			</div>
			{error && <FormInputError>{error}</FormInputError>}
		</FormInput>
	);
};

const DatePickerInput: React.FunctionComponent<DateInputProps> = ({
	control,
	name,
	error,
	...props
}) => {
	const {
		field: { onChange, value },
		fieldState: { error: controlError },
	} = useController({
		name,
		control,
	});
	const errorMessage = error || controlError?.message;
	const inputValue = value || null;

	return <DatePickerInputPlain name={name}
								 value={inputValue}
								 onChange={onChange}
								 error={errorMessage} {...props} />;

};

export default DatePickerInput;
